// PRODUCT_CATEGORIES ported verbatim from frontend-bk/erp/src/data/constants.js
//
// ⚠️ Bảng giá PHÁT SINH không còn ở đây (QĐ 0114, 10/09/2026): mảng
// SURCHARGE_CATALOG đã bỏ, mục phát sinh đọc từ máy chủ qua
// `useSurchargeCatalog` (surchargeApi.ts). Tệp này chỉ còn giữ KIỂU của một
// mục (để hai bên nói cùng một hình dạng) và danh mục GÓI tĩnh.
import { ACTION } from "@/lib/auth/actions";
import { hasPermission } from "@/lib/authv2/permissions";

/** Mục đích chụp của một gói (Hộ chiếu, Visa, hãng bay…) — giá đi kèm từng
 *  mục đích và THAY THẾ giá gói khi được chọn trên form đơn. */
export type ShootPurpose = {
  id: string;
  label: string;
  price: number;
  /**
   * Mã cỡ ảnh thẻ máy gắn sẵn cho mỗi tấm khách chốt khi đơn mang mục đích
   * này (QĐ 0285). Vắng = không gắn cỡ.
   */
  sizes?: string[];
  /**
   * Ô "Cần ảnh mặt mộc" (QĐ 0317): đơn mang mục đích này thì bảng quầy, thẻ
   * của trang điểm / thợ chụp và thanh chốt nhắc chụp một tấm mặt mộc TRƯỚC
   * khi trang điểm. Chỉ là lời nhắc, không đổi tiền. Vắng = không.
   */
  bareFace?: boolean;
};

export type SubProduct = {
  id: string;
  name: string;
  price: number;
  editPhotos: number;
  concepts?: string[];
  durationMin?: number;
  /** Số người của buổi mà gói bao gồm. Không khai/0 = gói không nói. */
  people?: number;
  /** Số người được trang điểm gói bao gồm (0253). Không khai = chưa khai. */
  makeupPeople?: number;
  /** Có danh sách này → form đơn bắt buộc chọn 1 mục đích; giá đơn = giá mục đích. */
  purposes?: ShootPurpose[];
};

/**
 * Thời lượng mặc định của một buổi chụp, tính bằng phút.
 *
 * Luật nghiệp vụ (AGENTS.md "Schedule Rules"): Profile 120', Ảnh thẻ 30' —
 * riêng CV 60' — Doanh nghiệp 60'. Gói nào khai `durationMin` thì gói ĐÈ lên
 * mặc định của nhóm; đó là đường CV lấy 60' mà không cần biết nó là CV.
 *
 * Vì sao phải có hàm này: form tạo đơn trước đây khởi tạo cứng 120' rồi không
 * ai đụng tới nữa, nên chọn Ảnh thẻ vẫn ra buổi chụp 2 tiếng và ăn mất 4 khe
 * của phòng chụp (SCH-01). Thời lượng phải đi THEO gói được chọn.
 */
export function shootDurationMin(
  category: string | null | undefined,
  productDurationMin?: number | null,
): number {
  if (typeof productDurationMin === "number" && productDurationMin > 0) {
    return productDurationMin;
  }
  switch ((category ?? "").toLowerCase()) {
    case "profile":
      return 120;
    case "idphoto":
      return 30;
    default:
      // corporate + nhóm tuỳ chỉnh chưa khai báo — 1 tiếng, cùng mốc với
      // lưới lịch (schedule/gridLayout.ts defaultSpanRows).
      return 60;
  }
}

/** 4 mục đích thủ tục giấy tờ dùng chung cho Ảnh Thẻ / CV — cùng giá trong 1 gói. */
const docPurposes = (price: number): ShootPurpose[] => [
  { id: "ho_chieu", label: "Hộ chiếu", price },
  { id: "visa", label: "Visa", price },
  { id: "bang_lai_xe", label: "Bằng lái xe", price },
  { id: "ho_so_khac", label: "Hồ sơ khác", price },
];

/** Riêng gói Ảnh Thẻ có thêm mục đích "cơ bản" đứng ĐẦU danh sách — chụp thẻ
 *  thường, không phục vụ một loại giấy tờ cụ thể. Cùng giá với 4 mục đích thủ
 *  tục của chính gói đó (CEO chốt 17/08/2026). Gói CV giữ nguyên 4 mục đích. */
const idPhotoPurposes = (price: number): ShootPurpose[] => [
  { id: "anh_the_co_ban", label: "Ảnh thẻ cơ bản", price },
  ...docPurposes(price),
];

/** Hãng bay cho gói Tiếp viên hàng không — giá theo hãng. Gói này luôn kèm
 *  makeup, không có biến thể không-make (CEO chốt 17/08/2026). */
const TVHK_PURPOSES: ShootPurpose[] = [
  { id: "vn_airline", label: "Vietnam Airline", price: 1_500_000 },
  { id: "vietjet", label: "Vietjet", price: 1_100_000 },
  { id: "sun_phu_quoc", label: "Sun Phú Quốc", price: 1_200_000 },
  { id: "bamboo_airway", label: "Bamboo Airway", price: 1_200_000 },
  { id: "hang_quoc_te", label: "Emirates / Cathay / EVA / Qatar", price: 2_500_000 },
  { id: "vietravel_airway", label: "Vietravel Airway", price: 1_200_000 },
];

export type StaffPayRule = { label: string; key: string; amount: number };
export type StaffRole = { role: string; payRules: StaffPayRule[] };

export type WorkflowStep = { step: number; name: string; icon: string };

export type ProductCategory = {
  id: string;
  name: string;
  icon: string;
  color: string;
  colorHex: string;
  /** Bản đậm của colorHex, đạt WCAG AA khi làm CHỮ trần trên nền sáng.
      colorHex vẫn dùng cho nền/viền/dot (nơi contrast không quan trọng). */
  textHex: string;
  badgeClass: string;
  subProducts: SubProduct[];
  staff: Record<string, StaffRole>;
  workflow: WorkflowStep[];
};

export const PRODUCT_CATEGORIES: Record<string, ProductCategory> = {
  profile: {
    id: "profile",
    name: "Profile Chuyên Nghiệp",
    icon: "camera",
    color: "var(--color-profile)",
    colorHex: "#8B6914",
    textHex: "#8B6914", // AA 5.09 — đã đủ đậm
    badgeClass: "badge-profile",
    subProducts: [
      {
        id: "pf_basic",
        name: "Profile Basic",
        price: 2300000,
        editPhotos: 5,
        concepts: ["toi_gian", "nen_trang", "nen_xam", "cv_basic"],
      },
      {
        id: "pf_modern",
        name: "Profile Hiện Đại",
        price: 3800000,
        editPhotos: 10,
        concepts: ["hien_dai", "toi_gian", "nen_trang", "chan_dung"],
      },
      {
        id: "pf_classic",
        name: "Profile Cổ Điển",
        price: 3800000,
        editPhotos: 10,
        concepts: ["co_dien", "toi_gian", "nen_trang", "chan_dung"],
      },
      {
        id: "pf_icon",
        name: "Profile Biểu Tượng",
        price: 4700000,
        editPhotos: 15,
        concepts: ["bieu_tuong", "vi_the", "co_dien", "hien_dai", "toi_gian", "nen_do"],
      },
      {
        id: "pf_cofounder",
        name: "Co-founder",
        price: 5500000,
        editPhotos: 12,
        concepts: ["doi", "co_dien", "hien_dai", "bieu_tuong", "vi_the"],
      },
    ],
    staff: {
      photographer: {
        role: "Thợ chụp + sửa ảnh",
        payRules: [
          { label: "Studio nửa ngày", key: "payStudio", amount: 900000 },
          { label: "Studio cả ngày", key: "payStudioFull", amount: 1800000 },
          { label: "Onsite nửa ngày", key: "payOnsite", amount: 1200000 },
          { label: "Onsite cả ngày", key: "payOnsiteFull", amount: 2400000 },
        ],
      },
      makeup: {
        role: "Thợ Make Up",
        payRules: [
          { label: "Mỗi khách", key: "payPerClient", amount: 150000 },
          { label: "Khách Premium", key: "payPremium", amount: 250000 },
          { label: "Phụ sửa tóc", key: "payHairOnly", amount: 100000 },
        ],
      },
    },
    workflow: [
      { step: 1, name: "Tư vấn → Đặt lịch", icon: "orders" },
      { step: 2, name: "Chuẩn bị (tẩy trang, đắp mask)", icon: "droplets" },
      { step: 3, name: "Make up + chỉnh tóc + chọn trang phục", icon: "sparkles" },
      { step: 4, name: "Chụp ảnh (nhiều concept)", icon: "camera" },
      { step: 5, name: "Chỉnh sửa ảnh (Capture One + Photoshop)", icon: "monitorCheck" },
      { step: 6, name: "Giao ảnh qua Drive", icon: "upload" },
    ],
  },

  idphoto: {
    id: "idphoto",
    name: "Ảnh Thẻ",
    icon: "creditCard",
    color: "var(--color-idphoto)",
    colorHex: "#7BA7C2",
    textHex: "#3f7291", // AA 5.21 (colorHex gốc 2.58 — mờ khi làm chữ)
    badgeClass: "badge-idphoto",
    subProducts: [
      {
        id: "at_basic",
        name: "Ảnh Thẻ (không make)",
        price: 490000,
        editPhotos: 1,
        purposes: idPhotoPurposes(450000),
      },
      {
        id: "at_makeup",
        name: "Ảnh Thẻ (có make)",
        price: 590000,
        editPhotos: 1,
        purposes: idPhotoPurposes(550000),
      },
      {
        id: "cv_basic_nomake",
        name: "CV Basic (không make)",
        price: 900000,
        editPhotos: 2,
        durationMin: 60,
        purposes: docPurposes(800000),
      },
      {
        id: "cv_basic_makeup",
        name: "CV Basic (có make)",
        price: 970000,
        editPhotos: 2,
        durationMin: 60,
        purposes: docPurposes(900000),
      },
      {
        id: "cv_pro_nomake",
        name: "CV Chuyên nghiệp (không make)",
        price: 1200000,
        editPhotos: 3,
        durationMin: 60,
        purposes: docPurposes(1100000),
      },
      {
        id: "cv_pro_makeup",
        name: "CV Chuyên nghiệp (có make)",
        price: 1300000,
        editPhotos: 3,
        durationMin: 60,
        purposes: docPurposes(1200000),
      },
      {
        // Không có hậu tố "(có make)" — gói đơn luôn kèm makeup, picker sẽ
        // không hiện tick chọn biến thể. Giữ id cũ để đơn đã lưu vẫn đọc được.
        id: "cv_tvhk_makeup",
        name: "Tiếp viên hàng không",
        price: 1300000,
        editPhotos: 3,
        durationMin: 60,
        purposes: TVHK_PURPOSES,
      },
      {
        id: "cv_grad_nomake",
        name: "CV Tốt nghiệp (không make)",
        price: 1200000,
        editPhotos: 3,
        durationMin: 60,
      },
      {
        id: "cv_grad_makeup",
        name: "CV Tốt nghiệp (có make)",
        price: 1300000,
        editPhotos: 3,
        durationMin: 60,
      },
    ],
    staff: {
      photographer: {
        role: "Thợ chụp ảnh thẻ",
        payRules: [
          { label: "Lương ca", key: "payPerShift", amount: 500000 },
          { label: "Mỗi khách", key: "payPerClient", amount: 30000 },
        ],
      },
      makeup: {
        role: "Thợ Make Up ảnh thẻ",
        payRules: [{ label: "Mỗi khách", key: "payPerClient", amount: 100000 }],
      },
    },
    workflow: [
      { step: 1, name: "Tiếp đón + Tư vấn", icon: "handHeart" },
      { step: 2, name: "Make up (nếu có)", icon: "sparkles" },
      { step: 3, name: "Chụp ảnh thẻ", icon: "camera" },
      { step: 4, name: "Chỉnh sửa nhanh (Evoto AI)", icon: "bot" },
      { step: 5, name: "In + giao file", icon: "printer" },
    ],
  },

  corporate: {
    id: "corporate",
    name: "Ảnh Doanh Nghiệp",
    icon: "building",
    color: "var(--color-corporate)",
    colorHex: "#A48BBE",
    textHex: "#7a5a99", // AA 5.6 (colorHex gốc 2.99 — mờ khi làm chữ)
    badgeClass: "badge-corporate",
    subProducts: [
      {
        id: "dn_basic",
        name: "Gói DN Basic (≤10 người)",
        price: 15000000,
        editPhotos: 50,
      },
      {
        id: "dn_standard",
        name: "Gói DN Standard (11-30)",
        price: 35000000,
        editPhotos: 150,
      },
      {
        id: "dn_premium",
        name: "Gói DN Premium (31-60)",
        price: 55000000,
        editPhotos: 300,
      },
      {
        id: "dn_enterprise",
        name: "Gói DN Enterprise (60+)",
        price: 80000000,
        editPhotos: 500,
      },
    ],
    staff: {
      photographer: {
        role: "Thợ chụp DN",
        payRules: [
          { label: "Studio nửa ngày", key: "payStudioHalf", amount: 900000 },
          { label: "Studio cả ngày", key: "payStudioFull", amount: 1800000 },
          { label: "Onsite nửa ngày", key: "payOnsiteHalf", amount: 1200000 },
          { label: "Onsite cả ngày", key: "payOnsiteFull", amount: 2400000 },
        ],
      },
      makeup: {
        role: "Thợ Make Up DN",
        payRules: [
          { label: "Mỗi người", key: "payPerPerson", amount: 150000 },
          { label: "Khách Premium", key: "payPremium", amount: 250000 },
        ],
      },
      assistant: {
        role: "Trợ lý set",
        payRules: [{ label: "Mỗi ngày", key: "payPerDay", amount: 500000 }],
      },
    },
    workflow: [
      { step: 1, name: "Gặp KH → Khảo sát nhu cầu", icon: "handHeart" },
      { step: 2, name: "Báo giá + Ký HĐ + Cọc", icon: "fileSignature" },
      { step: 3, name: "Lên lịch chụp theo đợt", icon: "calendarDays" },
      { step: 4, name: "Set up tại studio/onsite", icon: "clapperboard" },
      { step: 5, name: "Chụp theo nhóm", icon: "camera" },
      { step: 6, name: "Chỉnh sửa hàng loạt", icon: "monitorCheck" },
      { step: 7, name: "Nghiệm thu + Giao ảnh + Thanh toán", icon: "circleCheck" },
    ],
  },
};

/**
 * Nhóm nghiệp vụ của một mục phát sinh — ai là người GẶP khoản đó tại ca chụp:
 * "anh" thợ chụp/hậu kỳ, "make" makeup & tóc & phụ kiện làm đẹp, "trang_phuc"
 * đổi đồ. Lễ tân (và mọi người có quyền ghi phát sinh) thấy cả ba nhóm.
 */
export type SurchargeGroup = "anh" | "make" | "trang_phuc" | "my_pham";

/**
 * Nhóm HIỂN THỊ của mục phát sinh (cột surcharge.kind, QĐ 0130 — CEO chốt câu
 * 3a ngày 13/09/2026): sáu ngăn gập/mở trên bảng chọn, theo "Kiểu dịch vụ"
 * của bảng CEO. KHÁC `group`: `group` là ai GẶP khoản đó (rào ghi theo phòng),
 * `kind` chỉ là chỗ đứng trên màn — đổi `kind` không đổi ai được ghi.
 */
export type SurchargeKind = "trang_diem" | "toc" | "trang_phuc" | "nguoi" | "anh" | "my_pham" | "khac";

/** Một mục trong bảng giá phát sinh chuẩn (CEO chốt 17/08/2026). */
export type SurchargeCatalogItem = {
  id: string;
  label: string;
  /** Giá niêm yết VND. null = chưa có giá cố định — sale nhập tay khi lên đơn. */
  price: number | null;
  /** true = tính theo số lượng (đơn giá × N) khi lên đơn. */
  perUnit?: boolean;
  minQty?: number;
  /** "all" = mọi gói; hoặc giới hạn theo category id / product id cụ thể. */
  appliesTo: "all" | { categories?: string[]; products?: string[] };
  /**
   * Mục còn bán không (cột surcharge.is_active). Vắng mặt = đang bán.
   *
   * Câu "áp dụng cho" KHÔNG còn là một trường: nó là chữ cho người đọc, dựng
   * từ chính `appliesTo` bằng `appliesLabelOf` (surchargeLabel.ts) — máy chủ
   * không lưu, và giữ một bản chép tay là để nó lệch với phạm vi thật.
   */
  isActive?: boolean;
  /** Thứ tự hiện trong bảng chọn (cột surcharge.sort_order). */
  sortOrder?: number;
  /**
   * Khác rỗng = mã mà MÃ NGUỒN phụ thuộc: không xoá, không đổi mã, và mấy ô
   * rào (kiểu giá, số lượng tối thiểu, cờ ẩn) khoá luôn. Giao diện dùng để
   * hiện huy hiệu "Hệ thống" và khoá ô tương ứng.
   */
  systemKey?: string;
  /**
   * true = mỹ phẩm do kho GNW làm chủ (`source = gnw`, QĐ 0260): tên, giá,
   * đang bán hay ngừng đến từ kho; mọi cửa sửa tay máy chủ trả 409. Giao diện
   * hiện huy hiệu "Kho GNW quản" và bỏ các nút sửa.
   */
  fromGnwStock?: boolean;
  /** Nhóm nghiệp vụ — quyết định phòng ban nào thấy mục này trong bảng chọn
   *  (CEO chốt 19/08/2026). Danh sách nhóm người dùng thấy do MÁY CHỦ cấp —
 *  `visibleGroups` của GET /surcharge-catalog, đọc qua useSurchargeGroups. */
  group: SurchargeGroup;
  /** Nhóm hiển thị (ngăn trên bảng chọn). Vắng mặt = máy chủ cũ → xếp vào "Khác". */
  kind?: SurchargeKind;
  /**
   * true = KHÔNG hiện trong bảng chọn phát sinh và BE từ chối nếu gửi lên.
   * Mục vẫn ở lại bảng vì máy tính combo đọc GIÁ từ đây (đơn giá bậc đuôi
   * 11+); xoá mục là combo mất hẳn bậc đuôi.
   *
   * Từ 27/08/2026 phần ảnh 11+ chỉ nhập ở ô "Ảnh phát sinh", để cùng một số
   * ảnh không ra hai giá tuỳ nhân viên bấm cửa nào — chọn tay 11 ảnh ra
   * 880.000, ô ảnh phát sinh ra 979.000 (decision 0058).
   *
   * Gương của `HiddenFromPicker` trong backend/internal/core/order/surcharge_catalog.go.
   */
  hiddenFromPicker?: boolean;
  /**
   * true = nhân viên CSKH lên được khoản này trên đơn của khách sau khi chụp
   * (CEO chốt 7a 25/09/2026).
   *
   * Cờ NĂNG LỰC trên TỪNG MỤC, không phải tên nhóm: rổ `anh` trộn khoản sau
   * chụp (ảnh in, ảnh chỉnh) với khoản TẠI CA CHỤP (concept 3,6 triệu, phông
   * nền, hoa), nên mở cả rổ cho CSKH là rộng hơn hẳn phần việc của họ. Mặc
   * định tắt: món mới phải bật tay.
   *
   * Gương của `CskhSellable` trong backend/internal/core/order/surcharge_catalog.go.
   */
  cskhSellable?: boolean;
  /**
   * true = món kéo theo NGƯỜI TRANG ĐIỂM ngồi làm trong ca (migration 0360,
   * QĐ 0193): đơn có món này thì máy chia mở suất trang điểm, và lời khai
   * "0 người trang điểm" không chặn gán thợ. Cờ năng lực trên từng mục —
   * nhóm `make` còn chứa hàng bán mang về (lens, nails). Mặc định tắt.
   *
   * Gương của `NeedsMakeupCrew` trong backend/internal/core/surchargecatalog/model.go.
   */
  needsMakeupCrew?: boolean;
  /**
   * true = mỗi đơn vị số lượng của món này là THÊM MỘT NGƯỜI được trang điểm
   * (migration 0466, QĐ 0470). Thêm vào đơn thì người bán chia nữ / nam / bé
   * (bắt buộc), máy chủ cộng vào số người trang điểm của đơn.
   * Gương của `AddsMakeupFace` trong backend/internal/core/surchargecatalog/model.go.
   */
  addsMakeupFace?: boolean;
  /**
   * true = KHÔNG cho ngừng bán trên trang Sản phẩm (nút bật/tắt bị khoá).
   *
   * Năm mục ảnh chỉnh sửa (4 bậc thang + bậc đuôi 11+) là NGUỒN GIÁ của máy
   * tính combo `ExtraPhotoCombos` (backend extra_photo_pricing.go). Tắt một
   * bậc KHÔNG làm combo gãy — giá vẫn còn, máy vẫn thu đúng bậc đó qua ô "Ảnh
   * phát sinh" — mà tạo ra đúng cái decision 0058 sinh ra để dẹp: chọn tay thì
   * không còn mục, ô ảnh phát sinh thì vẫn bán, tức HAI CỬA HAI KẾT QUẢ cho
   * cùng một số ảnh. Thêm nữa `photosession` ghi `photo_extra` qua
   * `SetManagedLine` — đường đó không đi qua `normalizeSurchargeInput` nên
   * không bao giờ thấy cờ tắt.
   *
   * Đổi GIÁ năm mục này vẫn làm được như cũ; chỉ cấm TẮT.
   *
   * Gương của `LockedFromDisable` trong backend/internal/core/order/surcharge_catalog.go.
   */
  lockedFromDisable?: boolean;

  /**
   * Quà TẶNG kèm một lần bán mục này (QĐ 0118).
   *
   * Khác hẳn ưu đãi khuyến mãi: quà ở đây là MỘT PHẦN CỦA HÀNG ĐÃ BÁN —
   * khách trả tiền mục này để có nó — nên không đếm vào định mức đồ tặng của
   * đơn và không đi qua luật "hoặc mã giảm tiền, hoặc đồ tặng".
   *
   * `giftEditPhotos` cộng thẳng vào số ảnh được giao và KHÔNG chờ đơn thu đủ
   * tiền; `giftPrints` chỉ để hiển thị, máy không tự trừ vào đơn in.
   */
  giftEditPhotos?: number;
  giftPrints?: number;
  giftNote?: string;
  giftItems?: SurchargeGiftItem[];
};

/** Một món đồ tặng kèm mục phát sinh, trỏ về kho đồ tặng dùng chung. */
export type SurchargeGiftItem = {
  id: string;
  name: string;
  value: number;
};

/** Bản đồ mục đã ngừng bán, đọc từ settings `disabled_catalog_items`.
 *  Một namespace phẳng cho CẢ gói sản phẩm lẫn mục phát sinh — id hai bên
 *  không đụng nhau, y như `getEffectivePrice` đang tra chung một hàm. */
export type DisabledCatalogItems = Record<string, true>;

/** Mục/gói này đã ngừng bán chưa? Vắng key ⇒ mọi thứ đang bán. */
export function isCatalogItemDisabled(
  id: string,
  disabled: DisabledCatalogItems | undefined,
): boolean {
  return disabled?.[id] === true;
}

/**
 * Bỏ mục đã ngừng bán khỏi danh sách CHỌN.
 *
 * CHỈ dùng ở nơi người dùng đang chọn thứ MỚI. Nơi chỉ hiển thị/tra tên (bảng
 * giá, bộ lọc báo cáo, chi tiết đơn) phải để nguyên — đơn cũ mang mã đã tắt
 * vẫn phải đọc ra tên, cùng lý do `LEGACY_SURCHARGE_LABELS` (surchargeLabel.ts)
 * tồn tại.
 *
 * ⚠️ Hàm này CỐ Ý không có vế "giữ lại mã đơn đang mang". Nhét mã đã tắt ngược
 * vào danh sách trước `buildPackageOptions` sẽ ráp lại cặp "(có make)/(không
 * make)" cho đủ đôi ⇒ tick makeup hiện lại ⇒ người dùng tick qua tick lại là
 * CHỌN LẠI ĐƯỢC biến thể đã ngừng bán. Gói cũ của đơn phải được thêm SAU, dưới
 * dạng gói đơn đứng riêng — xem ProductPicker.
 */
export function sellableOnly<T extends { id: string }>(
  items: T[],
  disabled: DisabledCatalogItems | undefined,
): T[] {
  return items.filter((it) => !isCatalogItemDisabled(it.id, disabled));
}

/**
 * Tên GÓI theo mã. Mã lạ → trả chính mã (đơn cũ, gói tự chế).
 *
 * ⚠️ KHÔNG tra mục phát sinh nữa (QĐ 0114): tên mục phát sinh sống trong CSDL
 * và đổi được trên trang Sản phẩm, nên hàm thuần đồng bộ này không biết. Chỗ
 * nào cần tên phát sinh thì dùng `useSurchargeLabel()` (surchargeLabel.ts).
 */
export function productName(productId: string): string {
  if (!productId) return productId;
  for (const cat of Object.values(PRODUCT_CATEGORIES)) {
    const p = cat.subProducts.find((sp) => sp.id === productId);
    if (p) return p.name;
  }
  return productId;
}

/** Giá GÓI đang áp dụng — override đè giá tĩnh. Mã lạ trả 0.
 *
 *  ⚠️ KHÔNG tra mục phát sinh (QĐ 0114): giá phát sinh đọc từ bảng giá máy chủ
 *  (`useSurchargeCatalog`), không có bản tĩnh nào ở đây để rơi về. */
export function getEffectivePrice(
  productId: string,
  overrides: Record<string, number>,
): number {
  if (overrides[productId] !== undefined) return overrides[productId];
  for (const cat of Object.values(PRODUCT_CATEGORIES)) {
    const p = cat.subProducts.find((sp) => sp.id === productId);
    if (p) return p.price;
  }
  return 0;
}

/**
 * RBAC: who can edit product prices? — migrated to the permission claim.
 *
 * ⚠️ Lạc hậu từ 02/09/2026: khoá settings `product_prices` đã bỏ ở migration
 * 0202. Giá phát sinh nay ghi thẳng vào bảng `surcharge`. Hàm này chỉ còn dùng
 * cho giá GÓI. Câu dưới giữ lại làm bối cảnh lịch sử:
 *
 * Prices persist through PUT /settings/product_prices. That key has no
 * per-key override in the backend's keyActions map, so it falls back to
 * `settings.write` (admin + director). The previous role check also granted
 * a sale-department manager, which the server always rejected with 403 —
 * the pencil opened an editor whose save could never succeed. Sale is
 * read-only on this page by design.
 */
export function canEditProductPrices(): boolean {
  return hasPermission(ACTION.SettingsWrite);
}
